import { queryOptions } from "@tanstack/react-query";
import { config } from "@/config";

export interface ShareScene {
  scene_number: number;
  scene_name: string;
  seeing: string;
  hearing: string;
  props?: string;
  product?: string | null;
}

export interface ShareStoryboardImage {
  model: string;
  /** null when the API could not sign the object — render nothing for it. */
  imageUrl: string | null;
}

export interface SharePayload {
  v: 2;
  hook?: string | null;
  script?: string | null;
  scenes: ShareScene[];
  concept?: string | null;
  duration?: string | null;
  presenter?: string | null;
  background?: string | null;
  product?: string | null;
  adDescription?: string | null;
  rationale?: string | null;
  sequence?: string | null;
  opportunityTitle?: string | null;
  /** Absent on payloads cached before the v:2 rollout; empty unless READY. */
  storyboardImages?: ShareStoryboardImage[];
  updatedAt: string;
}

// ─── Fetch (public, no auth) ─────────────────────────────────────────────────
// The token is the script's id — the share page is rendered live from the DB,
// so there is no create step.

// Matches the backend cache TTL for GET /s/:token.
const SHARE_STALE_TIME_MS = 5 * 60 * 1000;

export const shareLinkKeys = {
  all: ["share-links"] as const,
  detail: (token: string) => [...shareLinkKeys.all, token] as const,
};

async function fetchShareLink(token: string): Promise<SharePayload> {
  const res = await fetch(`${config.apiUrl}/s/${encodeURIComponent(token)}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: { message?: string } };
    throw new Error(body.error?.message ?? `Share link not found (${res.status})`);
  }
  const json = await res.json() as { data: SharePayload };
  return json.data;
}

export function shareLinkQueryOptions(token: string) {
  return queryOptions({
    queryKey: shareLinkKeys.detail(token),
    queryFn: () => fetchShareLink(token),
    staleTime: SHARE_STALE_TIME_MS,
    retry: 1,
    enabled: !!token,
  });
}
